import { useState, useEffect, useCallback } from "react";
import Loading from "../../../components/utils/Loading";
import { getContainersList } from "../../../services/container";
import ContainerSettingsHeader from "./ContainerSettingsHeader";
import { Card } from "reactstrap";
import ContainerSettingsRow from "./ContainerSettingsRow";
import ContainerEdit from "../../container/ContainerEdit";
import { useTranslation } from "react-i18next";
import Pagination from "../../../components/utils/Pagination";
import EmptyState from "../../../components/EmptyState";
import StandardTable from "../../../ui/components/StandardTable";
import { toast } from "react-toastify";
import {
  duplicateContainerZone,
  LIST_QUERY_PARAMS,
} from "./zoneDuplicateUtils";

const ContainerSettings = ({ token, onCreateZone }) => {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [modalEdit, setModalEdit] = useState(false);
  const [sortByField, setSortByField] = useState("addressName");
  const [orderSortByField, setOrderSortByField] = useState("1");
  const [keywordFilter, setKeywordFilter] = useState("");
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultPage, setResultPage] = useState(0);
  const [duplicatingId, setDuplicatingId] = useState(null);

  const fetchContainers = useCallback(() => {
    return getContainersList(LIST_QUERY_PARAMS);
  }, []);

  const handleDuplicateZone = async (container) => {
    const id = container?._id;
    if (!id || duplicatingId) return;

    setDuplicatingId(id);
    try {
      await duplicateContainerZone({
        source: container,
        existingContainers: tableData,
      });

      const response = await fetchContainers();
      if (response.status === 200) {
        setTableData(response.data.data);
      }

      toast.success(t("settingsContainers.duplicateSuccess"), {
        className: "custom-toast",
      });
    } catch {
      toast.error(t("settingsContainers.duplicateError"), {
        className: "custom-toast",
      });
    } finally {
      setDuplicatingId(null);
    }
  };

  const sortData = (sortBy, order) => {
    const sorted = [...tableData].sort((a, b) => {
      const aValue = a[sortBy] ? a[sortBy].toString().toLowerCase() : "";
      const bValue = b[sortBy] ? b[sortBy].toString().toLowerCase() : "";

      if (aValue < bValue) return order === "1" ? -1 : 1;
      if (aValue > bValue) return order === "1" ? 1 : -1;
      return 0;
    });

    setTableData(sorted);
  };

  useEffect(
    () => {
      fetchContainers().then((response) => {
        if (response.status === 200) {
          setTableData(response.data.data);
          setIsLoading(false);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, fetchContainers],
  );

  useEffect(
    () => {
      sortData(sortByField, orderSortByField);
    },
    // eslint-disable-next-line
    [sortByField, orderSortByField],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [tableData]);

  // Filtrer les données par keywordFilter
  const filteredData = tableData.filter((container) => {
    if (!keywordFilter) return true;
    const searchTerm = keywordFilter.toLowerCase();
    const addressName = container.addressName?.toLowerCase() || "";
    const wasteName = container.wasteName?.toLowerCase() || "";
    return addressName.includes(searchTerm) || wasteName.includes(searchTerm);
  });

  const startIndex = (currentPage - 1) * recordPerPage;
  const endIndex = startIndex + recordPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);
  const hasContainers = paginatedData.length > 0;
  const hasAnyContainers = tableData.length > 0;
  const hasActiveFilters = Boolean(keywordFilter?.trim());
  const showEmptyState = !hasAnyContainers && !hasActiveFilters;

  if (token) {
    return isLoading ? (
      <Loading color="#F5F5F5" />
    ) : (
      <div className="w-full">
        <Card className="table border-0 shadow-none rounded-lg">
          <StandardTable
            fluidColCount={showEmptyState ? 1 : 4}
            fixedColWidth={showEmptyState ? 0 : 100}
            containerClassName={[
              "table-standard-container--zone-settings",
              showEmptyState ? "table-standard-container--list-empty" : null,
            ]
              .filter(Boolean)
              .join(" ")}
            columnAlignments={
              showEmptyState
                ? ["left"]
                : ["left", "left", "left", "left", "right"]
            }
            topBar={
              hasAnyContainers ? (
                <section className="table-standard-top-bar">
                  <div className="table-standard-top-bar-inner">
                    <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                      <div className="search-input-wrapper">
                        <i className="fas fa-search search-input-icon" />
                        <input
                          type="text"
                          className="search-input"
                          value={keywordFilter}
                          placeholder={`${t("Search")} (${t("Address")}, ${t("Waste Category")})`}
                          onChange={(event) =>
                            setKeywordFilter(event.target.value)
                          }
                        />
                      </div>
                    </div>
                    {onCreateZone ? (
                      <div className="create-button-wrapper">
                        <button
                          type="button"
                          className="create-button"
                          onClick={onCreateZone}
                        >
                          {t("settingsContainers.createNewZoneButton")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </section>
              ) : null
            }
            header={
              hasAnyContainers ? (
                <ContainerSettingsHeader
                  onSort={(field, order) => {
                    setSortByField(field);
                    setOrderSortByField(order);
                  }}
                  sortByField={sortByField}
                  orderSortByField={orderSortByField}
                />
              ) : null
            }
            bottom={
              hasAnyContainers ? (
                <div className="table-standard-bottom table-pagination-bottom">
                  <Pagination
                    totalEntry={filteredData.length}
                    recordPerPage={recordPerPage}
                    setRecordPerPage={setRecordPerPage}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    resultPage={resultPage}
                    setResultPage={setResultPage}
                  />
                </div>
              ) : null
            }
          >
            {hasContainers ? (
              paginatedData.map((container, index) => {
                if (!container) return null;
                return (
                  <div key={container._id || index}>
                    {modalEdit && formData._id === container._measurement && (
                      <ContainerEdit
                        tableData={tableData}
                        setTableData={setTableData}
                        editFormData={formData}
                        setEditFormData={setFormData}
                        modalEdit={modalEdit}
                        setModalEdit={setModalEdit}
                      />
                    )}
                    <ContainerSettingsRow
                      container={container}
                      setModalEdit={setModalEdit}
                      setFormData={setFormData}
                      onDuplicate={handleDuplicateZone}
                      isDuplicating={duplicatingId === container._id}
                    />
                  </div>
                );
              })
            ) : hasAnyContainers && hasActiveFilters ? (
              <div className="table-standard-row table-standard-row--empty">
                <div
                  className="table-standard-row-cell"
                  style={{ gridColumn: "span 4" }}
                >
                  {t("No results match your search")}
                </div>
                <div className="table-standard-row-cell table-standard-row-actions" />
              </div>
            ) : (
              <EmptyState
                title={t("emptyState.noContainer")}
                description={t("emptyState.hintContainer")}
                buttonText={
                  onCreateZone
                    ? t("settingsContainers.createNewZoneButton")
                    : undefined
                }
                onButtonClick={onCreateZone}
              />
            )}
          </StandardTable>
        </Card>
      </div>
    );
  }
};

export default ContainerSettings;
